// Session-list API fns — carved verbatim from `src/api/scribeAPIV2.ts`,
// `src/api/scribeAPI.ts` (v1), and `src/api/scribeAPIV2_2.ts` (which now
// re-export from here) with the D1 family. Transport comes from the
// `@heidi/api` configured-client registry; request paths, params, and
// bodies are byte-identical.

import { getScribeHttpClients } from '@heidi/api/client';
import {
  BaseStatus,
  SessionScope,
  SessionSortBy,
  type SessionStatus,
  Shared__enums__enums__SortOrder as SortOrder,
} from '@heidi/schema/ml-scribe';
import { isAxiosError } from 'axios';

import {
  type ChatListResponse,
  type SessionListItem,
  type SessionListResponse,
  type SessionProblemsResponse,
  type SessionSearchParams,
  type SessionSearchQueryParams,
  type SessionSearchResponse,
  type SessionStatusCountResponse,
  type SessionStatusCountsResponse,
  type SessionsResponse,
  type SimpleSessionResponse,
} from '../../contracts';
import { getSessionDataConfig } from '../config';

/**
 * v1 `GET /sessions/` — the legacy full-list fetch. Door name is
 * `getSessionsV1` because the v2 fn below already owns `getSessions`;
 * `src/api/scribeAPI.ts` re-exports it under its original name.
 */
export const getSessionsV1 = (
  pageSize?: number,
  page?: number,
  lastSessionCreatedAt?: string | null
) => {
  return getScribeHttpClients().scribeHttp.get<SessionsResponse>(`/sessions/`, {
    params: {
      page_size: pageSize,
      page,
      last_session_created_at: lastSessionCreatedAt,
    },
  });
};

export const getSessionsWithQueryParams = (params: SessionSearchQueryParams = {}) => {
  return getScribeHttpClients().scribeHttpV2.get<SessionsResponse>(`/sessions/`, {
    params,
  });
};

export const getSessions = ({
  pageSize,
  lastSessionCreatedAt,
}: {
  pageSize?: number;
  lastSessionCreatedAt?: string;
}) => {
  return getScribeHttpClients().scribeHttpV2.get<SessionsResponse>(`/sessions/`, {
    params: {
      page_size: pageSize,
      last_session_created_at: lastSessionCreatedAt,
    },
  });
};

export const getSessionsWithPagination = (params: SessionSearchParams) => {
  return getScribeHttpClients().scribeHttpV2.get<SessionSearchResponse>(
    '/sessions/search',
    {
      params: {
        q: params.q,
        page: params.page,
        page_size: params.pageSize,
        session_status: params.sessionStatus,
        date_from: params.dateFrom,
        date_to: params.dateTo,
        patient_profile_ids: params.patientProfileIds,
        untitled_text: params.untitledText,
        include_total_count: params.includeTotalCount,
        sort_by: params.sortBy,
        sort_order: params.sortOrder,
        is_urgent: params.isUrgent,
      },
      // FastAPI expects array params as key=val1&key=val2, not key[]=val1&key[]=val2
      paramsSerializer: {
        indexes: null, // This tells axios to serialize arrays without brackets
      },
    }
  );
};

export const getSessionCountBySessionStatus = (sessionStatus: SessionStatus) => {
  return getScribeHttpClients().scribeHttpV2.get<SessionStatusCountResponse>(
    '/sessions/status-count',
    {
      params: {
        session_status: sessionStatus,
      },
    }
  );
};

export const getTeamMemberSessionCountBySessionStatus = (input: {
  teamId: string;
  memberId: string;
  sessionStatus: SessionStatus;
}) => {
  return getScribeHttpClients().scribeHttpV2.get<SessionStatusCountResponse>(
    `/teams/${input.teamId}/members/${input.memberId}/sessions/status-count`,
    {
      params: {
        session_status: input.sessionStatus,
      },
    }
  );
};

/**
 * v2.2 `GET /sessions/status-counts?scope=all` — the whole-scope status-count
 * map backing the All Sessions status-filter dropdown. `scope=all` is the only
 * value the endpoint accepts today (422 otherwise).
 */
export const getSessionStatusCountsV2 = () => {
  return getScribeHttpClients().scribeHttpV2_2.get<SessionStatusCountsResponse>(
    '/sessions/status-counts',
    {
      params: {
        scope: SessionScope.all,
      },
    }
  );
};

export interface SessionsListParams {
  scope: SessionScope;
  page_size: number;
  cursor?: string;
  sort_by?: SessionSortBy;
  sort_order?: SortOrder;
  session_status?: SessionStatus[];
  date_from?: string;
  date_to?: string;
  patient_profile_ids?: string[];
  /** Ignored for scope=me. */
  clinician_ids?: string[];
  q?: string;
  /**
   * Server-side urgency filter. `true` ⇒ only urgent sessions; omitted ⇒ no
   * urgency filter (unchanged behaviour). See docs/contracts/session-urgency.md.
   */
  is_urgent?: boolean;
}

/** v2.2 `GET /sessions` — the unified session list endpoint. */
export const getSessionsListV2 = (params: SessionsListParams) => {
  return getScribeHttpClients().scribeHttpV2_2.get<SessionListResponse>('/sessions', {
    params,
    // FastAPI expects array params as key=val1&key=val2, not key[]=val1&key[]=val2
    paramsSerializer: {
      indexes: null,
    },
  });
};

/**
 * `GET /v2/chat/sessions/:id/chats` — session-scoped chat list (ARCFE-1003).
 * The v2 client's base already carries the version segment, so the path is
 * byte-identical to the pre-move call.
 */
export const getSessionChats = (
  sessionId: string | undefined,
  page: number,
  pageSize: number
) => {
  return getScribeHttpClients().scribeHttpV2.get<ChatListResponse>(
    `/chat/sessions/${sessionId}/chats`,
    { params: { page, page_size: pageSize } }
  );
};

/** `GET /sessions/:id/problems` — problem-based charting read (ARCFE-1003). */
export const getSessionProblems = (sessionId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<SessionProblemsResponse>(
    `/sessions/${sessionId}/problems`
  );
};

// ---------------------------------------------------------------------------
// Linked sessions by patient profile
// ---------------------------------------------------------------------------

/**
 * `GET /sessions/?patient_profile_id=…` — the caller's own sessions linked to a
 * patient profile. Carved verbatim from `src/api/scribeAPIV2.ts` (which now
 * consumes this module); the `@domain session` tag retires with the carve.
 * Path, trailing slash and param name are byte-identical.
 */
export const getSessionsByPatientProfileId = (patientProfileId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<SessionsResponse>(`/sessions/`, {
    params: {
      patient_profile_id: patientProfileId,
    },
  });
};

/**
 * `GET /teams/{t}/members/{m}/sessions?patient_profile_id=…` — the reviewing
 * cohort's arm of the same read. Carved verbatim from `src/api/scribeAPIV2.ts`,
 * where it carried `@domain workspace`; it is a SESSIONS read (its response is
 * `SessionsResponse` and its own-sessions twin above is `@domain session`), and
 * its by-id neighbour `getTeamMemberSessionById` was already carved here at D3.
 * The tag retires with the carve. Path, params and positional argument order
 * are byte-identical.
 *
 * Deliberately still on v2, even after COLAB-1 routes the own-arm through v2.2:
 * this is the session-review dashboard's teammate-scoped read, not a
 * shared-directory surface. Its `kinde_gp_id == teamMemberId` filter IS the
 * intended semantic — the caller wants what teammate M can see, not what the
 * caller can see. See openspec change `colab-1-linked-sessions-via-v2-2`,
 * design decision 1.
 */
export const getTeamMemberSessionsByPatientProfileId = (
  teamId: string,
  teamMemberId: string,
  patientProfileId: string
) => {
  return getScribeHttpClients().scribeHttpV2.get<SessionsResponse>(
    `/teams/${teamId}/members/${teamMemberId}/sessions`,
    {
      params: {
        patient_profile_id: patientProfileId,
      },
    }
  );
};

/**
 * v2.2 flag-gated read of the caller's linked sessions for one patient
 * profile. Under `release-scribe-shared-patient-directory` (COLAB-1), the
 * drawer's own-arm reader routes through here so a workspace-visible profile
 * surfaces every session the caller can see across own, team, and direct-share
 * — the v2 twin above filters to `kinde_gp_id == caller` and renders empty for
 * shared profiles whose sessions were captured by teammates.
 *
 * `page_size: 50` with `sort_by=created_at, sort_order=DESC` loads the first
 * page of sessions; callers follow `next_cursor` via subsequent calls with the
 * `cursor` param to page through the full history. The sort pin matches sister
 * v2.2 caller `useQueryRecentSessionEngagement`; without it a >50-session
 * patient could get an arbitrary first page depending on the server default.
 *
 * The adapter shapes each v2.2 `SessionListItemView` into a
 * `SimpleSessionResponse` (the v2 wire item other consumers of
 * `useQueryLinkedSessionsByProfileId` — the past-sessions button, the
 * link/dropdown lists — read fields off of), so both flag branches return a
 * `SessionsResponse`-shaped envelope. Each v2.2 item also keeps
 * `clinician_name` (the session owner, which the v2 item lacks), typed as
 * `string | null`. The card's `session_gist` slot takes the wire `session_gist`
 * when present, else `session_name` (older backends omit the gist; this aligns
 * the drawer with how v2.2's All Sessions list shows session titles). `status` and `is_consent`
 * are absent from the v2.2 wire and set to their default (`BaseStatus.CREATED`,
 * `null`) so the returned shape satisfies `SimpleSessionResponse` without
 * inventing values.
 *
 * `next_cursor` is returned alongside the `SessionsResponse` envelope so the
 * caller (the drawer's session-history section) can follow it with a second
 * call carrying `cursor`. Truncation signals are no longer emitted — cursor
 * following replaces silent data loss. The pre-check-403 signal is unchanged.
 * Payloads carry no patient identifiers (PHI-adjacent in a RUM stream
 * correlated to a clinician session). See openspec changes
 * `colab-1-linked-sessions-via-v2-2` and
 * `colab-384-follow-next-cursor-in-drawer-linked-sessions`.
 */
const LINKED_SESSIONS_V2_2_PAGE_SIZE = 50;

/** A v2.2 item in the v2 shape, keeping the session owner the v2 shape lacks. */
type LinkedSessionV2_2 = SimpleSessionResponse &
  Required<Pick<SessionListItem, 'clinician_name'>>;

export const getSessionsByPatientProfileIdV2_2 = async (
  patientProfileId: string,
  cursor?: string
): Promise<{
  data: Omit<SessionsResponse, 'sessions'> & {
    sessions: LinkedSessionV2_2[];
  };
  next_cursor: string | null;
}> => {
  try {
    const { data } = await getSessionsListV2({
      scope: SessionScope.all,
      patient_profile_ids: [patientProfileId],
      page_size: LINKED_SESSIONS_V2_2_PAGE_SIZE,
      sort_by: SessionSortBy.created_at,
      sort_order: SortOrder.DESC,
      ...(cursor != null ? { cursor } : {}),
    });

    const nextCursor = data.next_cursor ?? null;

    const sessions: LinkedSessionV2_2[] = data.sessions.map((s) => ({
      ...s,
      // Prefer the editable gist once the v2.2 wire carries it; fall back to
      // the generation-time name for older backends.
      session_gist: s.session_gist ?? s.session_name ?? null,
      clinician_name: s.clinician_name ?? null,
      // Absent from the v2.2 wire but required on SimpleSessionViewV2.
      // `status` takes the schema default (CREATED); `is_consent` is
      // `null` rather than the schema default `false` — an asserted
      // "no consent" would mislead any future consumer that reads this
      // field as truth.
      status: BaseStatus.CREATED,
      is_consent: null,
    }));

    return {
      data: {
        sessions,
        count: sessions.length,
        has_next_page: nextCursor != null,
      },
      next_cursor: nextCursor,
    };
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 403) {
      getSessionDataConfig().trackLinkedSessionsSignal?.(
        'linked_sessions_profile_forbidden',
        { flag_on: true }
      );
    }
    throw err;
  }
};
